import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, FileText, AlertTriangle, Download, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/api";
import LegalFooter from "@/components/LegalFooter";

/**
 * /legal and /legal/:docId — Public legal documents index + individual doc
 * viewer. Every doc renders a prominent DRAFT banner on top.
 *
 * We fetch from /api/legal/documents so the docs stay a single source of
 * truth on the backend (`services/legal_docs.py`). No hard-coded copy in
 * this component.
 */
export default function Legal() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const url = docId ? `${API}/legal/documents/${docId}` : `${API}/legal/documents`;
    axios
      .get(url)
      .then((r) => {
        if (cancelled) return;
        if (docId) {
          setDoc(r.data);
        } else {
          setDocuments(r.data?.documents || []);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? "Document not found." : "Could not load legal documents.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [docId]);

  const printDoc = () => {
    if (typeof window !== "undefined") window.print();
  };

  const downloadBundle = () => {
    if (typeof window !== "undefined") {
      window.open(`${API}/legal/bundle.pdf`, "_blank", "noopener,noreferrer");
    }
  };

  if (docId) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="legal-doc-page">
        <div className="max-w-3xl mx-auto px-5 py-8 print:py-4">
          <div className="flex items-center justify-between mb-4 print:hidden">
            <Button
              variant="ghost"
              className="text-zinc-400 hover:text-white"
              onClick={() => navigate("/legal")}
              data-testid="legal-doc-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All documents
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-white/10 text-zinc-300 hover:bg-zinc-900"
              onClick={printDoc}
              data-testid="legal-doc-print"
            >
              <Download className="w-4 h-4 mr-2" />
              Print / PDF
            </Button>
          </div>

          {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
          {error && (
            <Card className="bg-red-500/5 border-red-500/30">
              <CardContent className="p-6 text-red-300 text-sm">{error}</CardContent>
            </Card>
          )}

          {doc && (
            <>
              <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 flex gap-3" data-testid="legal-draft-banner">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs leading-relaxed">
                  <p className="font-bold text-amber-300 uppercase tracking-wide mb-1">Draft — attorney review required</p>
                  <p className="text-amber-200/80">{doc.draft_banner}</p>
                </div>
              </div>

              <header className="mb-6 border-b border-white/5 pb-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4D88FF] font-bold mb-2">{doc.category}</p>
                <h1 className="text-3xl sm:text-4xl font-bold mb-3">{doc.title}</h1>
                <p className="text-zinc-400 text-sm leading-relaxed">{doc.summary}</p>
                <p className="text-[11px] text-zinc-600 mt-3">
                  Version {doc.version} · Effective {doc.effective_date}
                </p>
              </header>

              <div className="space-y-6" data-testid="legal-doc-sections">
                {(doc.sections || []).map((s) => (
                  <section key={s.heading}>
                    <h2 className="text-lg font-bold text-white mb-2">{s.heading}</h2>
                    <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{s.body}</p>
                  </section>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-white/5 text-xs text-zinc-500 space-y-1">
                <p>© Link-Lite Data LLC. All rights reserved.</p>
                <p>Questions? Email <a className="text-[#4D88FF] hover:underline" href="mailto:link@linklitepro.com">link@linklitepro.com</a>.</p>
              </div>
            </>
          )}
        </div>
        <LegalFooter />
      </div>
    );
  }

  // Index view
  const byCategory = documents.reduce((acc, d) => {
    (acc[d.category] = acc[d.category] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-black text-white" data-testid="legal-index-page">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white mb-4"
          onClick={() => navigate("/")}
          data-testid="legal-index-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Home
        </Button>

        <header className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-[#0047FF]/10">
              <FileText className="w-6 h-6 text-[#4D88FF]" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#4D88FF] font-bold">Link Ride · Legal Library</p>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">Documents</h1>
          <p className="text-sm text-zinc-400 max-w-xl leading-relaxed">
            The agreements, policies, and disclosures that govern Link Ride. Each document is versioned and dated.
            All documents are DRAFTS pending attorney review.
          </p>
          <Button
            onClick={downloadBundle}
            className="mt-4 bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0 font-bold"
            data-testid="legal-download-bundle"
          >
            <Download className="w-4 h-4 mr-2" />
            Download all as PDF
          </Button>
        </header>

        {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {!loading && !error && (
          <div className="space-y-8">
            {Object.entries(byCategory).map(([cat, docs]) => (
              <section key={cat}>
                <h2 className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-3">{cat}</h2>
                <div className="space-y-2">
                  {docs.map((d) => (
                    <Link
                      key={d.id}
                      to={`/legal/${d.id}`}
                      className="block p-4 rounded-lg bg-[#0c0c0c] border border-white/5 hover:border-[#0047FF]/40 hover:bg-[#0047FF]/5 transition-colors group"
                      data-testid={`legal-index-item-${d.id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-bold text-white mb-1 group-hover:text-[#7AADFF] transition-colors">{d.title}</p>
                          <p className="text-xs text-zinc-500 line-clamp-2">{d.summary}</p>
                          <p className="text-[10px] text-zinc-600 mt-1.5">v{d.version} · {d.effective_date}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-[#4D88FF] shrink-0 transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
      <LegalFooter />
    </div>
  );
}
